import { Dialog, DialogContent, DialogTitle, Button, Box, Tooltip, List, ListItem, CircularProgress, Divider, Typography } from '@mui/material'
import ReactAudioPlayer from 'react-audio-player'
import ChatDocument from '../../../ChatDocument'
import Contact from '../../../Contact'
import Location from '../../../Location'
import Poll from '../../../Poll'
import dayjs from 'dayjs'
import { transformTimeData } from '../ChatList'
import api from '../../../../api';
import ReactionsList from '../ReactionsList'
import useNotification from '../../../Notification'
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import { TranscriptionButton } from '../../../Button/TranscriptionButton';
import { useState } from 'react';
import { formatGroupParticipantsUpdateText, isGroupParticipantsUpdateMessage } from '../../../../utils/scout'

import ArticleIcon from '@mui/icons-material/Article';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import OndemandVideoIcon from '@mui/icons-material/OndemandVideo';
import RedoIcon from '@mui/icons-material/Redo';

export const isMessageForwarded = (message) => {
    return Boolean(message?.isForwarded || message?.is_forwarded || message?.contextInfo?.isForwarded)
}

const ForwardedMessageLabel = () => (
    <Box className='scout-forwarded-message-label'>
        <RedoIcon fontSize='inherit' />
        <span>Encaminhada</span>
    </Box>
)

const DateSeparator = ({ date }) => {
    return (
        <Box sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            margin: '16px 0',
            width: '100%'
        }}>
            <Divider sx={{ flexGrow: 1, bgcolor: 'rgba(0, 0, 0, 0.12)' }} />
            <Typography 
                variant="caption" 
                sx={{ 
                    padding: '0 16px', 
                    color: 'rgba(0, 0, 0, 0.6)',
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    borderRadius: '12px',
                    fontSize: '0.75rem'
                }}
            >
                {dayjs(date * 1000).format('DD/MM/YYYY')}
            </Typography>
            <Divider sx={{ flexGrow: 1, bgcolor: 'rgba(0, 0, 0, 0.12)' }} />
        </Box>
    );
};

const EditedMessage = ({ children, type, onClick }) => {
    return (
        <span onClick={onClick} className='scout-edited-message-span'>
            {children || 'Mensagem apagada.'}
            {type === 'EDITED' ? <span>Editada</span> : null}
        </span>
    )
}

const doctype_dict = {
   'documentMessage': {'label': 'Documento', icon: <ArticleIcon />}
  ,'audioMessage'   : {'label': 'Áudio', icon: <VolumeUpIcon />}
  ,'imageMessage'   : {'label': 'Imagem', icon: <InsertPhotoIcon />}
  ,'videoMessage'   : {'label': 'Video', icon: <OndemandVideoIcon />}
}

const DownloadMedia = ({ message, setParent, messages, parent }) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleDownload = async () => {
    if (isLoading) return; // Prevent multiple clicks
    
    setIsLoading(true);
    try {
      await getMedia(message, setParent, messages, parent);
    } finally {
      setIsLoading(false);
    }
  };

  const doctype = doctype_dict[message.messageType]

  return ( 
    <Box 
      sx={{ 
        position: 'relative', 
        display: 'inline-block',
        cursor: isLoading ? 'not-allowed' : 'pointer',
        maxHeight: '20rem', // Ensure the container respects the image height
        width: 'auto'
      }}
    >
      {message.thumbnail ? <img 
        height={'20rem'} 
        src={`data:image/jpg;base64,${message.thumbnail}`} 
        alt='imagem' 
        onClick={handleDownload}
        style={{ 
          cursor: isLoading ? 'not-allowed' : 'pointer',
          opacity: isLoading ? 0.6 : 1,
          transition: 'opacity 0.2s',
          height: '20rem', // Explicitly set height in style as well
          width: '100%', // Maintain aspect ratio
          display: 'block'
        }}
      /> : 
      <Box onClick={handleDownload} sx={{background: '#c5c5c5', borderRadius: '.25rem', padding: '0.5rem 0.25rem', fontWeight: 'bold'}}> 
          <Box>Clique para carregar {doctype.label}</Box>
          <Box>Título: {message.title}</Box>
          <Box display='flex' sx={{justifyContent: 'space-between'}}>
            <Box>{Math.round(message.size / (1024*1024) * 100) / 100} Mb</Box>
            <Box>{doctype.icon}</Box>
          </Box>

      </Box>
      }
      {isLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: '50%',
            width: '40px',
            height: '40px'
          }}
        >
          <CircularProgress size={24} sx={{ color: 'white' }} />
        </Box>
      )}
    </Box>
  );
}

const filterLatestReactions = (reactions) => {
    /**
     * Filters a list of reaction objects, keeping only the latest reaction
     * for each unique contact, based on the timestamp.
     *
     * Args:
     *     reactions: An array of reaction objects.
     *
     * Returns:
     *     An array of reaction objects, with only the latest reaction per contact.
     */
  
    const latestReactions = {};
    for (const reaction of reactions) {
      const contact = reaction.message.contact;
      const timestamp = reaction.timestamp;
  
      if (!(contact in latestReactions) || timestamp > latestReactions[contact].timestamp) {
        latestReactions[contact] = reaction;
      }
    }
  
    return Object.values(latestReactions);
}

export const reactionInfo = (reactionList, setPrevState) => {
  if (reactionList.length === 0) return null;

  let sortedReactions = {};
  filterLatestReactions(reactionList).forEach((r) => {
    if (!sortedReactions[r.message.reaction]) {
      sortedReactions[r.message.reaction] = {
        messages: [r],
        count: 1,
        reaction: r.message.reaction,
      }
    } else {
      sortedReactions[r.message.reaction].messages.push(r);
      sortedReactions[r.message.reaction].count++;
    }
  });

  // Create an array from all sortedReaction keys sorted by count
  let reactionKeys = Object.keys(sortedReactions).sort((a, b) => sortedReactions[b].count - sortedReactions[a].count);


  // Get the 4 first reactionKeys:
    const keys = reactionKeys.slice(0, 4).join('');

  return (
    <span
      onClick={() =>
        setPrevState({
          reactionsList: {
            r: sortedReactions,
            k: reactionKeys,
          },
        })
      }
    >
      {keys}
    </span>
  );
};

const getMedia = async (message, setParent, messages, parent) => {
    const params = new URLSearchParams({
      limit: '1',
      skip: '0',
      slimResponse: '0',
      chatMessageId: message.id,
    });
    
    try {
        const res = await api.get(
            `api/enterprises/${parent?.activeAccount?.enterprise}/scout_accounts/${parent?.activeAccount?.id}/messages/${parent?.activeChat?.id}/?${params.toString()}`, 
            { timeout: 50000 }
        );
        
        if (res.status === 200 && res.data.length > 0) {
            const newMessage = res.data[0];

            // Create updated messages array with the new message data
            const updatedMessages = messages.map(m => {
                if (m.id === message.id) {
                    // Replace the old message with the new one, preserving existing properties
                    return { ...m, ...newMessage };
                }
                return m;
            });
            
            // Update the parent state with the new messages array
            setParent({ 
              messages: updatedMessages,
              showCRMSidebar: true,
            });
            
        } else {
          if (res.status === 429) {
              useNotification(
                  'Limite de solicitações atingido',
                  'Você atingiu o limite de solicitações. Por favor, tente novamente mais tarde.',
                  'danger'
              );
          } else {
            console.error("No media found for this message or an error occurred.");
            useNotification({
                type: 'error',
                message: `Mídia não encontrada para esta mensagem. [${res.status}]`,
                });
          }
      }
    } catch (error) {
        console.error("Error fetching media:", error);
        useNotification({
            type: 'error',
            message: `Erro ao buscar mídia. ${error.message}`,
        });
    }
};


const openImage = (base64Data) => {
  // base64 sem o prefixo "data:image/jpg;base64,"

  // converter base64 para binário
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);

  // criar Blob
  const blob = new Blob([byteArray], { type: "image/jpeg" });

  // gerar URL temporária
  const blobUrl = URL.createObjectURL(blob);

  // abrir em nova aba
  window.open(blobUrl, "_blank");
}

const addParticipantDirectoryEntry = (directory, identifier, name) => {
    const normalizedName = String(name || '').trim()
    if (!normalizedName || !identifier) return

    const raw = String(identifier).trim()
    const digits = raw.split('@')[0].replace(/:\d+$/, '').replace(/\D/g, '')
    ;[raw, raw.toLowerCase(), digits].filter(Boolean).forEach((key) => {
        if (!directory.has(key)) directory.set(key, normalizedName)
    })
}

const buildParticipantDirectory = (messages = []) => {
    const directory = new Map()

    messages.forEach((entry) => {
        const participantName = String(entry?.pushName || entry?.push_name || entry?.sender || '').trim()
        if (!participantName) return

        addParticipantDirectoryEntry(directory, entry?.participant, participantName)
        addParticipantDirectoryEntry(directory, entry?.key?.participant, participantName)
    })

    return directory
}

const parseEventResponseMessage = (message) => {
    const payload = message?.eventResponseMessage || message?.message?.eventResponseMessage || message?.event_response_details || message || {}
    const labels = {
        1: 'Vou',
        2: 'Não',
        3: 'Talvez',
        '1': 'Vou',
        '2': 'Não',
        '3': 'Talvez',
        GOING: 'Vou',
        NOT_GOING: 'Não',
        MAYBE: 'Talvez',
    }
    const responseValue = payload.response && typeof payload.response === 'object' ? payload.response.response : payload.response
    const response = typeof responseValue === 'string' ? responseValue.trim() : responseValue
    const label = payload.responseLabel || labels[response] || labels[String(response || '').toUpperCase()]

    return label || 'Evento'
}

const EventMessage = ({ message }) => {
    const [open, setOpen] = useState(false)
    const details = message?.event_details || message || {}
    const responses = Array.isArray(details.eventResponses)
        ? details.eventResponses
        : Array.isArray(message?.eventResponses)
        ? message.eventResponses
        : Array.isArray(message?.pollUpdates)
        ? message.pollUpdates
        : []
    const groups = ['Vou', 'Talvez', 'Não'].map(label => ({
        label,
        responses: responses.filter(response => parseEventResponseMessage(response).endsWith(label))
    }))

    return (
        <Box>
            <Typography fontWeight="bold">{details.title || details.name || 'Evento'}</Typography>
            {details.description && <Typography variant="body2">{details.description}</Typography>}
            {(details.startTimeLabel || details.startLabel) && <Typography variant="body2">Início: {details.startTimeLabel || details.startLabel}</Typography>}
            {(details.endTimeLabel || details.endLabel) && <Typography variant="body2">Fim: {details.endTimeLabel || details.endLabel}</Typography>}
            {(details.location_name || details.locationLabel) && <Typography variant="body2">Local: {details.location_name || details.locationLabel}</Typography>}
            <Button size="small" onClick={() => setOpen(true)}>Mostrar respostas</Button>
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Respostas do evento</DialogTitle>
                <DialogContent>
                    {groups.map(group => (
                        <Box key={group.label} sx={{ mb: 2 }}>
                            <Typography fontWeight="bold">{group.label} ({group.responses.length})</Typography>
                            {group.responses.length ? (
                                <List dense>
                                    {group.responses.map((response, index) => (
                                        <ListItem key={`${group.label}-${index}`}>
                                            {response.name || response.pushName || response.displayName || response.id || 'Participante'}
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography variant="body2" color="text.secondary">Nenhuma resposta nesta opção.</Typography>
                            )}
                        </Box>
                    ))}
                </DialogContent>
            </Dialog>
        </Box>
    )
}

export const parseMessage = (message, editList, showEditList = () => { }, parent = null, setParent = () => { }, messages = []) => {
    
    if (editList.length > 0) {
        const lastMessage = editList[editList.length - 1]
        return (
            <EditedMessage
                onClick={async () => { await showEditList([message, ...editList]) }}
                type={lastMessage.type}>
                {lastMessage.message}
            </EditedMessage>)
    }

    if (isGroupParticipantsUpdateMessage(message)) {
        return <>{formatGroupParticipantsUpdateText(message, {
            connectedPhone: parent?.activeAccount?.phone_number,
            connectedName: parent?.activeAccount?.name,
            participantDirectory: buildParticipantDirectory(messages),
        })}</>
    }

    switch (message.messageType) {

        case 'extendedTextMessage':
            return <>{message.message} </>
        case 'conversation':
            return <>{message.message} </>
        case 'imageMessage':
            if (message.base64 === null || message.base64 === undefined || message.base64.length < 1) {
              return <DownloadMedia message={message} setParent={setParent} messages={messages} parent={parent} />
            } else if (message.caption) {
                return <><img  style={{cursor: 'pointer'}} onClick={()=>openImage(message.base64)} height={'10rem'} src={`data:image/jpg;base64,${message.base64}`} alt='imagem' /> <p>{message.caption}</p></>
            } else {
                return <img style={{cursor: 'pointer'}} onClick={()=>openImage(message.base64)} height={'10rem'} src={`data:image/jpg;base64,${message.base64}`} alt='imagem' />
            }
        case 'videoMessage':
            if (message.base64 === null || message.base64 === undefined || message.base64.length < 1) {
              return <DownloadMedia message={message} setParent={setParent} messages={messages} parent={parent} />
            } else if (message.caption) {
                return <><video src={`data:video/mp4;base64,${message.base64}`} controls width="600"> Video </video> <p>{message.caption}</p></>
            } else {
                return <video src={`data:video/mp4;base64,${message.base64}`} controls width="600"> Video </video>
            }
        case 'stickerMessage':
            return <img src={`data:image/webp;base64,${message.base64}`} alt='imagem' className='scout-conversation-sticker' />
        case 'documentWithCaptionMessage':
        case 'documentMessage':
            if (message.base64 === null || message.base64 === undefined || message.base64.length < 1) 
              return <DownloadMedia message={message} setParent={setParent} messages={messages} parent={parent} />
            return (
                <ChatDocument user={1}
                    name={message.title}
                    message={message}
                    src={`data:${message.mimetype};base64,${message.base64}`}
                    base64
                />
            )
        case 'audioMessage':
            if (message.base64 === null || message.base64 === undefined || message.base64.length < 1) 
              return <DownloadMedia message={message} setParent={setParent} messages={messages} parent={parent} />
            return <ReactAudioPlayer
                src={`data:audio/x-wav;base64,${message.base64}`}
                controls
                preload={'metadata'}
                style={{ height: '2rem' }}
            />;
        case 'templateMessage':
            return <>{message.templateMessage?.hydratedTemplate?.hydratedContentText || ''}</>
        case 'contactMessage':
            return <Contact
                fullName={message.fullName}
                title={message.title}
                phone={message.phone}
                email={message.email}
                address={message.address}
            />
        case 'locationMessage':
            return <Location
                name={message.name}
                address={message.address}
                latitude={message.latitude}
                longitude={message.longitude}
            />
        case 'pollCreationMessageV3':
            return <Poll
                question={message.name}
                options={message.options}
            />
        case 'eventMessage':
            return <EventMessage message={message} />
        case 'eventResponseMessage':
        case 'encEventResponseMessage':
            return <></>
        case 'editedMessage':
        case 'protocolMessage':
            return <>{message?.message || message?.protocolMessage?.editedMessage?.conversation || message?.conversation || 'Essa mensagem foi apagada.'}</>

    }
    return ""
}

const ScoutMessageComponent = ({message, index, chatType, messages, parent, setPrevState, setParent, searchWidget, owner, conversation, setLoading, previousMessage}) => {
  const isGroupParticipantsUpdate = isGroupParticipantsUpdateMessage(message)

  // Check if we need to show a date separator
  const shouldShowDateSeparator = () => {
    if (!previousMessage) return false; // No separator for the first message
    
    const currentDate = dayjs(message.timestamp * 1000).format('DD/MM/YYYY');
    const previousDate = dayjs(previousMessage.timestamp * 1000).format('DD/MM/YYYY');
    
    return currentDate !== previousDate;
  };

  return ( 
    <>
    {shouldShowDateSeparator() && <DateSeparator date={message.timestamp} />}
    <ListItem disableGutters
        key={`individual-message-id-${index}`}
        className={`scout-window-content-item ${isGroupParticipantsUpdate ? 'system' : message.fromMe ? 'right' : 'left'} ${message.restricted == true ? 'restricted' : ''}`}
        onClick={ async () => { 
            if (!searchWidget) return;

            const account = parent?.accounts.find(account => owner.endsWith(account.phone_number));
            const res = await api.get(
                `api/enterprises/${account?.enterprise}/scout_accounts/${account?.id}/conversation/${conversation?.info?.id}/`
            );
            if (res.status == 200) {
                const activeChat = { account_id: account.id, ...res.data[0] };
                setParent({
                    activeAccount: account,
                    activeChat: activeChat,
                    chatMessageId: message.id,
                    showSearch: false,
                    showCRMSidebar: false,
                    showAnalyticsSidebar: false,
                })
            }


        }}
    >
        <Box className='scout-conversation-box'>
            {isMessageForwarded(message) && <ForwardedMessageLabel />}
            {chatType === 'GROUP' && !isGroupParticipantsUpdate && <Box sx={{ fontSize: '0.6em', fontWeight: 'bold' }}>@{message.pushName}</Box>}
            {parseMessage(
                message, 
                messages.filter(msg => msg.type && msg.protocolId === message.id), 
                searchWidget ? () => {} : (data) => setPrevState({ editList: data }),
                parent,
                setPrevState,
                messages,
            )}
            <TranscriptionButton 
              message_id={message.id} 
              message_transcription={message.transcription}
              message_transcription_status={message.transcription_status} 
              message_type={message.messageType}
              transcriptionEndpointURL={`api/enterprises/${parent?.activeAccount?.enterprise}/scout_accounts/${parent?.activeAccount?.id}/conversation/${parent?.activeChat?.id}/audio/${message?.id}/transcribe/`}
              updateMessage={(message_id, status, key, transcription) => {
                setPrevState({
                  messages: messages.map((m) => {
                    if (m.id === message_id) {
                      m.transcription_status = status;
                      m.transcription_key = key;
                      m.transcription = transcription;
                    }
                    return m;
                  }),
                });
              }}
            />
            <Tooltip title={dayjs(message.timestamp * 1000).format('DD/MM/YYYY HH:mm')}>
                <Box className='scout-conversation-time'>{transformTimeData(message.timestamp)}
                </Box>
            </Tooltip>
            <Box className='scout-conversation-reaction'>
                {reactionInfo(
                messages.filter(msg => msg.messageType === 'reactionMessage' && msg.message.reactedMessageId == message.id),
                setPrevState
                )}
            </Box>
        </Box>
    </ListItem>
    </>
  );
};

export default ScoutMessageComponent;
